import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppService, RuntimeAppSetting, SaveRuntimeAppSettingRequest } from '../../app.service';
import { SessionTimeoutService } from '../../auth/services/session-timeout.service';
import { AlertService } from '../../common/alert.service';
import { LoadingService } from '../../common/loading.service';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { finalize, mergeMap } from 'rxjs';

@Component({
  selector: 'app-settings',
  templateUrl: './app-settings.component.html',
  styleUrls: ['./app-settings.component.scss']
})
export class AppSettingsComponent implements OnInit {
  form!: FormGroup;
  saving = false;
  saved = false;

  backfilling = false;
  confirmBackfill = false;

  // these get updated as user types
  uiTimeoutMs = 0;
  uiWarningMs = 0;

  constructor(
    private fb: FormBuilder,
    private service: AppService,
    private sessionTimeout: SessionTimeoutService,
    private alert: AlertService,
    private spinner: LoadingService
  ) {
    this.form = this.fb.group(
      {
        uiTimeoutMinutes: [15, [Validators.required, Validators.min(1)]],
        uiWarningMinutes: [1, [Validators.required, Validators.min(1)]],
      },
      { validators: this.warningLessThanTimeout() }
    );
  }

  ngOnInit() {
    this.service.GetRuntimeAppSettings({ name: '' }).subscribe(res => {
      const get = (k: string, d: number) =>
        parseInt(res.settings?.find(s => s.name === k)?.value ?? '', 10) || d;

      const timeoutMs = get('uiTimeoutMs', 15 * 60 * 1000);
      const warningMs = get('uiTimeoutWarningMs', 60 * 1000);

      this.uiTimeoutMs = timeoutMs;
      this.uiWarningMs = warningMs;

      // convert ms → minutes for form
      this.form.patchValue({
        uiTimeoutMinutes: Math.round(timeoutMs / 60000),
        uiWarningMinutes: Math.round(warningMs / 60000),
      });
    });

    // keep ms display in sync as user edits
    this.form.valueChanges.subscribe(v => {
      this.uiTimeoutMs = (v.uiTimeoutMinutes ?? 0) * 60_000;
      this.uiWarningMs = (v.uiWarningMinutes ?? 0) * 60_000;
    });
  }

  public onSaveTimeoutSettings(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alert.warning('Please correct the errors before saving.');
      return;
    }

    const uiTimeoutMinutes = this.form.value.uiTimeoutMinutes!;
    const uiWarningMinutes = this.form.value.uiWarningMinutes!;
    const uiTimeoutMs = uiTimeoutMinutes * 60_000;
    const uiTimeoutWarningMs = uiWarningMinutes * 60_000;

    this.saving = true;
    this.spinner.show();

    const reqTimeout = new SaveRuntimeAppSettingRequest();
    reqTimeout.setting = { name: 'uiTimeoutMs', value: String(uiTimeoutMs) } as RuntimeAppSetting;

    const reqWarning = new SaveRuntimeAppSettingRequest();
    reqWarning.setting = { name: 'uiTimeoutWarningMs', value: String(uiTimeoutWarningMs) } as RuntimeAppSetting;

    this.service.SaveRuntimeAppSetting(reqTimeout).pipe(
      mergeMap(r1 => {
        if (!r1.success) throw new Error(r1.message ?? 'Failed to save uiTimeoutMs.');
        return this.service.SaveRuntimeAppSetting(reqWarning);
      }),
      finalize(() => { this.saving = false; this.spinner.hide(); })
    ).subscribe({
      next: (r2) => {
        if (!r2.success) {
          this.alert.warning(r2.message ?? 'Failed to save uiTimeoutWarningMs.');
          return;
        }

        const safeWarning = Math.min(uiTimeoutWarningMs, uiTimeoutMs - 1000);
        this.sessionTimeout.reconfigureAndReset(uiTimeoutMs, safeWarning);
        sessionStorage.setItem('runtimeAppSettings:v1', JSON.stringify({
          uiTimeoutMs, uiTimeoutWarningMs: safeWarning
        }));

        this.alert.success('Timeout settings saved.');
        this.saved = true;
        setTimeout(() => (this.saved = false), 2000);

        // Mark clean after a successful save
        this.form.markAsPristine();
        this.form.updateValueAndValidity();
      },
      error: (err) => {
        console.error(err);
        this.alert.error(err?.message ?? 'Error saving timeout settings.');
      }
    });
  }


  private warningLessThanTimeout(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const t = group.get('uiTimeoutMinutes')?.value;
      const w = group.get('uiWarningMinutes')?.value;
      if (t == null || w == null) return null; // other validators handle required
      return w < t ? null : { range: true };
    };
  }

  public backfillMissingCCOs(): void {
    if (this.backfilling) return;

    const proceed = confirm(
      'Run backfill for missing CCOs?\n\nThis will look up CCOs for all PCP/HRAT/Maternity referrals with empty CCO and update them.'
    );
    if (!proceed) return;

    this.backfilling = true;
    this.spinner.show();

    this.service.BackfillMissingCcos()
      .pipe(finalize(() => {
        this.backfilling = false;
        this.spinner.hide();
      }))
      .subscribe({
        next: (res) => {
          const success = res?.success ?? res?.Success ?? false;
          const message = res?.message ?? res?.Message ?? 'Backfill completed.';
          if (success) {
            this.alert.success(message);
          } else {
            // If API returns non-success but 200, still inform user
            this.alert.info(message);
          }
        },
        error: (err) => {
          console.error(err);
          this.alert.error('An error occurred while backfilling CCOs.');
        }
      });
  }
}
